// src/components/shared/PageLoader.tsx
import { memo, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/img/logo_gold.png";

const loadingStages = [
  { min: 0, text: "Preparing your experience..." },
  { min: 14, text: "Setting the tone..." },
  { min: 28, text: "Shaping the moment..." },
  { min: 42, text: "Arranging the details..." },
  { min: 58, text: "Softening the atmosphere..." },
  { min: 72, text: "Bringing everything together..." },
  { min: 88, text: "Almost ready..." },
  { min: 98, text: "Your moment is ready." },
];

export const PageLoader = memo(() => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          window.clearInterval(timer);
          return 100;
        }

        const increment = prev < 35 ? 1.8 : prev < 70 ? 1.15 : prev < 92 ? 0.65 : 0.28;

        return Math.min(prev + increment, 100);
      });
    }, 90);

    return () => window.clearInterval(timer);
  }, []);

  const currentMessage = useMemo(() => {
    return [...loadingStages].reverse().find((stage) => progress >= stage.min)?.text;
  }, [progress]);

  const revealProgress = Math.min((progress / 98) * 100, 100);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black text-white">
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(circle at 50% 35%, rgba(249,202,36,0.16), transparent 34%),
            radial-gradient(circle at 50% 100%, rgba(249,202,36,0.08), transparent 42%),
            linear-gradient(180deg, #050505 0%, #000 100%)
          `,
        }}
      />

      <div className="relative z-10 flex w-full max-w-sm flex-col items-center px-8 text-center rounded-2xl">
        <div className="relative mb-9 h-32 w-32 md:h-40 md:w-40">
          <img
            src={logo}
            alt="LRNSY"
            className="absolute inset-0 h-full w-full object-contain opacity-[0.08] grayscale"
          />

          <motion.div
            className="absolute inset-0"
            animate={{
              opacity: progress >= 98 ? 1 : 0.96,
            }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            style={{
              WebkitMaskImage: `linear-gradient(
        45deg,
        #000 0%,
        #000 ${revealProgress}%,
        transparent ${Math.min(revealProgress + 12, 100)}%,
        transparent 100%
      )`,
              maskImage: `linear-gradient(
        45deg,
        #000 0%,
        #000 ${revealProgress}%,
        transparent ${Math.min(revealProgress + 12, 100)}%,
        transparent 100%
      )`,
              WebkitMaskSize: "100% 100%",
              maskSize: "100% 100%",
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
            }}
          >
            <img
              src={logo}
              alt=""
              className="h-full w-full object-contain drop-shadow-[0_0_34px_rgba(249,202,36,0.35)]"
            />
          </motion.div>

          <motion.div
            className="absolute inset-0 rounded-full"
            animate={{
              opacity: [0.18, 0.42, 0.18],
              scale: [0.96, 1.06, 0.96],
            }}
            transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background: "radial-gradient(circle, rgba(249,202,36,0.18), transparent 62%)",
            }}
          />
        </div>

        <div className="h-8">
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessage}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
              className="text-[11px] font-bold uppercase tracking-[0.32em] text-white/70"
            >
              {currentMessage}
            </motion.p>
          </AnimatePresence>
        </div>

        <div className="mt-8 w-full">
          <div className="mb-2 flex justify-between text-[9px] uppercase tracking-[0.28em] text-white/35">
            <span>Preparing template</span>
            <span>{Math.floor(progress)}%</span>
          </div>

          <div className="h-[2px] w-full overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.35 }}
              style={{
                background:
                  "linear-gradient(90deg, rgba(249,202,36,0.55), rgba(255,255,255,0.95), rgba(249,202,36,0.75))",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
